import { MutationData } from '@passionware/monads';
import { ReactNode, type ComponentProps } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import {
  DialogBody,
  DialogContentLayout,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { FormRootElement } from 'v5/design-system/Form';
import { DialogErrorFooter } from './DialogErrorFooter';
import { FormSaveButton } from './FormSaveButton';
import { useFormSubmitHotkeys } from 'v5/platform/react/useFormSubmit';

export interface FormDialogContentProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onSubmit'> {
  disabled?: boolean;
  onSubmit: () => void;

  /**
   * Dialog open state
   */
  open: boolean;

  /**
   * Dialog title
   */
  title: ReactNode;

  /**
   * Mutation data for handling loading states
   */
  mutation: MutationData<unknown, unknown>;

  /**
   * Save button text (defaults to "Save")
   */
  saveText?: string;

  /**
   * Dialog description for screen readers
   */
  description?: string;

  /**
   * Additional footer content
   */
  footerContent?: ReactNode;

  /**
   * Custom save button slot. If provided, overrides the default FormSaveButton.
   * Receives the same props as FormSaveButton for consistency.
   */
  saveButtonSlot?: ReactNode;

  /** The save button's look; the app's primary by default. */
  saveVariant?: ComponentProps<typeof FormSaveButton>['variant'];

  renderDialogBody?: (body: ReactNode) => ReactNode;
  renderHeader?: (title: ReactNode) => ReactNode;
}

export function FormDialogContent({
  style,
  disabled,
  onSubmit,
  open,
  title,
  mutation,
  saveText,
  children,
  description,
  footerContent,
  saveButtonSlot,
  saveVariant,
  className,
  renderDialogBody = children => (
    <DialogBody className="space-y-6">{children}</DialogBody>
  ),
  renderHeader = title => (
    <DialogHeader>
      <DialogTitle>{title}</DialogTitle>
    </DialogHeader>
  )
}: FormDialogContentProps) {
  const { t } = useTranslation('patterns');
  const defaultSaveText = t('FormDialogContent.save');
  const displaySaveText = saveText || defaultSaveText;
  // Handle Cmd+Enter (or Ctrl+Enter) to submit
  const ref = useFormSubmitHotkeys<HTMLDivElement>(onSubmit);

  return (
    <DialogContentLayout
      className={cn('max-w-(--container-xl)', className)}
      style={style}
      ref={ref}
    >
      <DialogDescription className="sr-only">
        {description || title}
      </DialogDescription>

      <FormRootElement onSubmit={onSubmit} className="contents">
        {renderHeader(title)}
        {renderDialogBody(children)}
        <DialogFooter className="mt-auto">
          {footerContent || <DialogErrorFooter mutation={mutation} />}
          {saveButtonSlot || (
            <FormSaveButton
              variant={saveVariant}
              disabled={disabled}
              mutation={mutation}
              saveText={displaySaveText}
            />
          )}
        </DialogFooter>
      </FormRootElement>
    </DialogContentLayout>
  );
}
